/**
 * API de pacientes (tenant-scoped).
 */
import { apiFetch } from "./client";

/** CNV = certificado de nacido vivo: lo único que tiene un recién nacido. */
export type TipoDocumento = "DNI" | "CE" | "PAS" | "CNV";

export type Parentesco =
  | "MADRE"
  | "PADRE"
  | "ABUELO"
  | "ABUELA"
  | "TIO"
  | "TIA"
  | "HERMANO"
  | "HERMANA"
  | "TUTOR"
  | "OTRO";

export const PARENTESCOS: { value: Parentesco; label: string }[] = [
  { value: "MADRE", label: "Madre" },
  { value: "PADRE", label: "Padre" },
  { value: "ABUELA", label: "Abuela" },
  { value: "ABUELO", label: "Abuelo" },
  { value: "TIA", label: "Tía" },
  { value: "TIO", label: "Tío" },
  { value: "HERMANA", label: "Hermana" },
  { value: "HERMANO", label: "Hermano" },
  { value: "TUTOR", label: "Tutor legal" },
  { value: "OTRO", label: "Otro" },
];

export interface Apoderado {
  id: number;
  persona_id: number;
  nombre_completo: string;
  tipo_documento: string;
  num_documento: string | null;
  telefono: string | null;
  email: string | null;
  parentesco: Parentesco;
  es_principal: boolean;
  puede_autorizar: boolean;
  observacion: string | null;
  /** Con valor, además se atiende acá: es apoderado Y paciente. */
  paciente_id: number | null;
}

export interface ApoderadoIn {
  persona_id?: number | null;
  tipo_documento?: TipoDocumento;
  num_documento?: string | null;
  nombres?: string | null;
  ap_paterno?: string | null;
  ap_materno?: string | null;
  telefono?: string | null;
  email?: string | null;
  direccion?: string | null;
  parentesco: Parentesco;
  es_principal?: boolean;
  puede_autorizar?: boolean;
  observacion?: string | null;
}

export interface MenorACargo {
  paciente_id: number;
  nombre_completo: string;
  parentesco: Parentesco;
}

/** Alguien que ya está en el sistema, sea paciente o solo contacto. */
export interface PersonaEncontrada {
  persona_id: number;
  tipo_documento: TipoDocumento;
  num_documento: string | null;
  nombres: string;
  ap_paterno: string;
  ap_materno: string | null;
  nombre_completo: string;
  fecha_nacimiento: string | null;
  sexo: Sexo | null;
  telefono: string | null;
  email: string | null;
  direccion: string | null;
  /** null = todavía no es paciente; abrirle historia no lo duplica. */
  paciente_id: number | null;
  menores_a_cargo: MenorACargo[];
}
export type Sexo = "M" | "F" | "O";
export type GrupoSanguineo =
  | "A+"
  | "A-"
  | "B+"
  | "B-"
  | "AB+"
  | "AB-"
  | "O+"
  | "O-";

/** Cómo está el acceso de este paciente a la app. */
export interface AccesoPortalEstado {
  tiene_cuenta: boolean;
  activo: boolean;
  /** Por qué no puede tenerla: es menor, o no trajo documento. */
  impedimento: string | null;
}

export interface Paciente {
  id: number;
  persona_id: number;
  tipo_documento: TipoDocumento;
  /** null = por regularizar. Solo ocurre en menores. */
  num_documento: string | null;
  nombres: string;
  ap_paterno: string;
  ap_materno: string | null;
  fecha_nacimiento: string | null;
  sexo: Sexo | null;
  email: string | null;
  telefono: string | null;
  ubigeo: string | null;
  direccion: string | null;
  nombre_completo: string;
  grupo_sanguineo: GrupoSanguineo | null;
  consentimiento_lpdp: boolean;
  /** Cómo lo otorgó: FISICO, VERBAL o DIGITAL. null = pendiente o antiguo. */
  consentimiento_medio: "FISICO" | "VERBAL" | "DIGITAL" | null;
  consentimiento_fecha: string | null;
  activo: boolean;
  created_at: string;
  /** Calculados por el backend: la interfaz no hace aritmética de fechas. */
  edad_anios: number | null;
  es_menor: boolean;
  apoderados: Apoderado[];
  acceso_portal: AccesoPortalEstado;
}

export interface PacienteCreate {
  tipo_documento: TipoDocumento;
  /** Puede quedar vacío en un menor: se regulariza después. */
  num_documento?: string | null;
  nombres: string;
  ap_paterno: string;
  ap_materno?: string | null;
  fecha_nacimiento?: string | null;
  sexo?: Sexo | null;
  email?: string | null;
  telefono?: string | null;
  ubigeo?: string | null;
  direccion?: string | null;
  grupo_sanguineo?: GrupoSanguineo | null;
  /**
   * Cómo lo otorgó el paciente. Quien registra declara un hecho, no acepta en
   * su nombre. null = pendiente de regularizar.
   */
  consentimiento_medio?: "FISICO" | "VERBAL" | "DIGITAL" | null;
  /** Obligatorio si el paciente es menor de edad. */
  apoderados?: ApoderadoIn[];
}

export interface PacienteUpdate {
  /** Solo para COMPLETAR el de quien no tenía. No reescribe uno existente. */
  tipo_documento?: TipoDocumento;
  num_documento?: string | null;
  nombres?: string;
  ap_paterno?: string;
  ap_materno?: string | null;
  fecha_nacimiento?: string | null;
  sexo?: Sexo | null;
  email?: string | null;
  telefono?: string | null;
  ubigeo?: string | null;
  direccion?: string | null;
  grupo_sanguineo?: GrupoSanguineo | null;
  activo?: boolean;
}

export interface RenicConsultaResponse {
  dni: string;
  nombres: string;
  ap_paterno: string;
  ap_materno: string;
  fecha_nacimiento: string | null;
  sexo: "M" | "F" | null;
  fuente: "RENIEC" | "MOCK";
}

export function listPacientes(
  search?: string,
  limit: number = 100,
  todos: boolean = false,
): Promise<Paciente[]> {
  const params = new URLSearchParams();
  if (search) params.set("q", search);
  params.set("limit", String(limit));
  if (todos) params.set("todos", "true");
  return apiFetch<Paciente[]>(`/v1/pacientes?${params.toString()}`);
}

export function getPaciente(id: number): Promise<Paciente> {
  return apiFetch<Paciente>(`/v1/pacientes/${id}`);
}

export function createPaciente(payload: PacienteCreate): Promise<Paciente> {
  return apiFetch<Paciente>("/v1/pacientes", { method: "POST", body: payload });
}

export function updatePaciente(
  id: number,
  payload: PacienteUpdate,
): Promise<Paciente> {
  return apiFetch<Paciente>(`/v1/pacientes/${id}`, {
    method: "PATCH",
    body: payload,
  });
}

/** Consulta RENIEC (mock en dev). */
export function reniecLookup(dni: string): Promise<RenicConsultaResponse> {
  return apiFetch<RenicConsultaResponse>("/v1/pacientes/_reniec", {
    method: "POST",
    body: { dni },
  });
}

export function buscarPersona(
  tipo_documento: string,
  num_documento: string,
): Promise<PersonaEncontrada | null> {
  const q = new URLSearchParams({ tipo_documento, num_documento });
  return apiFetch<PersonaEncontrada | null>(`/v1/pacientes/_persona?${q.toString()}`);
}

export function listApoderados(pacienteId: number): Promise<Apoderado[]> {
  return apiFetch<Apoderado[]>(`/v1/pacientes/${pacienteId}/apoderados`);
}

export function agregarApoderado(
  pacienteId: number,
  payload: ApoderadoIn,
): Promise<Apoderado> {
  return apiFetch<Apoderado>(`/v1/pacientes/${pacienteId}/apoderados`, {
    method: "POST",
    body: payload,
  });
}

export function quitarApoderado(pacienteId: number, vinculoId: number): Promise<void> {
  return apiFetch<void>(`/v1/pacientes/${pacienteId}/apoderados/${vinculoId}`, {
    method: "DELETE",
  });
}

export function menoresACargo(pacienteId: number): Promise<MenorACargo[]> {
  return apiFetch<MenorACargo[]>(`/v1/pacientes/${pacienteId}/a-cargo`);
}


/** Alta o reseteo del acceso al portal. Devuelve la clave para dictarla. */
export interface AccesoPortal {
  usuario_id: number;
  num_documento: string;
  password_temporal: string | null;
  ya_tenia_cuenta: boolean;
}

export function darAccesoPortal(pacienteId: number): Promise<AccesoPortal> {
  return apiFetch<AccesoPortal>(`/v1/pacientes/${pacienteId}/acceso-portal`, {
    method: "POST",
  });
}

/**
 * Le quita el acceso a la app sin borrarle la cuenta.
 *
 * Quitar SÍ es una decisión aparte; dar acceso no lo era, y por eso la cuenta
 * nace con el registro del paciente.
 */
export function quitarAccesoPortal(pacienteId: number): Promise<void> {
  return apiFetch<void>(`/v1/pacientes/${pacienteId}/acceso-portal`, {
    method: "DELETE",
  });
}
